import {
  getAudienceSegmentsMenuList,
  getSamsungDMPAudienceSegmentPartnerMenuList,
} from '~/src/dash/api/audience-segment';
import { getBidderLabelsAsIMenuList } from '~/src/dash/api/bidder-labels';
import { getCampaignsMenuList } from '~/src/dash/api/campaigns';
import { getChannelLabelsAsIMenuList } from '~/src/dash/api/channel-labels';
import { getUniqueLineItemAliasesMenuList, IReport } from '~/src/dash/api/console-api';
import {
  getContentChannelFilterOptions,
  getContentNetworkFilterOptions,
  getOrigContentChannelFilterOptions,
  getOrigContentNetworkFilterOptions,
} from '~/src/dash/api/content';
import { getCountryGroupsIMenuList } from '~/src/dash/api/country-group';
import { getDestinationsMenuList } from '~/src/dash/api/destinations';
import { getSmallBiddersList } from '~/src/dash/api/filters/bidders';
import { getInventoryRoutesAsIMenuList } from '~/src/dash/api/inventory_routes';
import { getNetworksMenuList } from '~/src/dash/api/network';
import { getAdserverOrderMenuList, getUniqueOrderAliasesMenuList } from '~/src/dash/api/order';
import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { generateCountriesMenuList, generateStatesMenuList } from '~/src/dash/utils/countries';
import {
  deviceEnvironmentsAsIMenu,
  generateDeviceEnvironmentSubtypes,
} from '~/src/dash/utils/device-environments';
import { createReportingDeviceTypesMenuList } from '~/src/dash/utils/devices';
import { getLivestreamMenuList } from '~/src/dash/utils/livestream-options';
import {
  generateBidderTierMenuList,
  generateTrueFalseMenuList,
} from '~/src/dash/utils/report-fields';
import { createAllStreamTypesMenuList } from '~/src/dash/utils/stream-types';
import {
  generateDemandSourcesList,
  getBidderGroupsList,
  getChannelGroupsList,
  getDealsWithAlphanumericID,
  getSmallSitesList,
} from '~/src/dash/views/common/dashboards-consts';

interface IReportFilter {
  key: string;
  apiKey: string;
  apiType: EReportFilterApiTypes;
  isApiKeyInReportFilterConfig?: boolean;
  getList?: (
    session: ISessionState,
    user: IUserState,
    params: Record<string, any>,
  ) => Promise<Record<string, IMenuList>>;
  list?: IMenuList;
}

export enum EReportFilterApiTypes {
  string = 0,
  number,
  stringList,
  numberList,
}

export const REPORT_FILTERS: IReportFilter[] = [
  {
    key: 'site_ids',
    getList: (session: ISessionState, user: IUserState) => getSmallSitesList(session, user),
    apiKey: 'SiteIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'bidder_ids',
    getList: (session: ISessionState, user: IUserState) => getSmallBiddersList(session, user),
    apiKey: 'BidderIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'channel_group_ids',
    getList: (session: ISessionState, user: IUserState) => getChannelGroupsList(session, user),
    apiKey: 'ChannelGroupIDS',
    apiType: EReportFilterApiTypes.numberList,
  },
  {
    key: 'bidder_group_ids',
    getList: (session: ISessionState, user: IUserState) => getBidderGroupsList(session, user),
    apiKey: 'BidderGroupsIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'countries',
    list: generateCountriesMenuList(),
    apiKey: 'Countries',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'country_group_ids',
    getList: (session: ISessionState) => getCountryGroupsIMenuList(session),
    apiKey: 'CountryGroupIDs',
    isApiKeyInReportFilterConfig: true,
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'deal_ids',
    apiKey: 'DealIDs',
    apiType: EReportFilterApiTypes.string,
  },
  {
    key: 'deal_names',
    getList: (session: ISessionState, _) => getDealsWithAlphanumericID(session),
    apiKey: 'DealNames',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'biddercodes',
    getList: (session: ISessionState, user: IUserState) =>
      generateDemandSourcesList(session, user, '', false, false),
    apiKey: 'BidderCodes',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'deviceenvs',
    list: deviceEnvironmentsAsIMenu(),
    apiKey: 'DeviceEnvs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'device_subtypes',
    getList: (session: ISessionState, user: IUserState) =>
      generateDeviceEnvironmentSubtypes(session, user),
    apiKey: 'DeviceSubtypes',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'devicetypes',
    list: createReportingDeviceTypesMenuList(ReportTypeID.AuctionQuickReport),
    apiKey: 'DeviceTypes',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'inventory_route_ids',
    getList: (session: ISessionState, user: IUserState) =>
      getInventoryRoutesAsIMenuList(session, user),
    apiKey: 'InventoryRouteIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'destination_ids',
    getList: (session: ISessionState) => getDestinationsMenuList(session),
    apiKey: 'DestinationIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'channel_label_ids',
    getList: (session: ISessionState, user: IUserState) =>
      getChannelLabelsAsIMenuList(session, user),
    apiKey: 'ChannelLabelIDs',
    isApiKeyInReportFilterConfig: true,
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'bidder_label_ids',
    getList: (session: ISessionState, user: IUserState) =>
      getBidderLabelsAsIMenuList(session, user),
    apiKey: 'BidderLabelIDs',
    isApiKeyInReportFilterConfig: true,
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'bidder_tier_filter',
    list: generateBidderTierMenuList({} as IReport),
    apiKey: 'BidderTierFilter',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'order_aliases',
    getList: (session: ISessionState) => getUniqueOrderAliasesMenuList(session),
    apiKey: 'OrderAliases',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'line_item_aliases',
    getList: (session: ISessionState) => getUniqueLineItemAliasesMenuList(session),
    apiKey: 'LineItemAliases',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'states',
    list: generateStatesMenuList('', []),
    apiType: EReportFilterApiTypes.stringList,
    apiKey: 'States',
  },
  {
    key: 'stream_types',
    list: createAllStreamTypesMenuList(),
    apiKey: 'StreamTypes',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'livestream',
    list: getLivestreamMenuList(),
    apiKey: 'Livestream',
    apiType: EReportFilterApiTypes.number,
    isApiKeyInReportFilterConfig: true,
  },
  {
    key: 'network_ids',
    getList: (session: ISessionState) => getNetworksMenuList(session),
    apiKey: 'NetworkIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'is_partial_ad_break',
    list: generateTrueFalseMenuList(),
    apiKey: 'IsPartialAdBreak',
    apiType: EReportFilterApiTypes.string,
    isApiKeyInReportFilterConfig: true,
  },
  {
    key: 'dmas',
    apiKey: 'DMAs',
    apiType: EReportFilterApiTypes.string,
  },
  {
    key: 'adomains',
    apiKey: 'Adomains',
    apiType: EReportFilterApiTypes.string,
  },
  {
    key: 'advertiser_ids',
    apiKey: 'AdvertiserIDs',
    isApiKeyInReportFilterConfig: true,
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'order_ids',
    getList: (session: ISessionState) => getAdserverOrderMenuList(session),
    apiKey: 'OrderIDs',
    isApiKeyInReportFilterConfig: true,
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'campaign_ids',
    getList: (session: ISessionState, _user: IUserState, params: Record<string, any>) =>
      getCampaignsMenuList(session, params),
    apiKey: 'CampaignIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'segment_ids',
    getList: (session: ISessionState) => getAudienceSegmentsMenuList(session),
    apiKey: 'SegmentIDs',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'samsungdmp_segment_partners',
    getList: (session: ISessionState) => getSamsungDMPAudienceSegmentPartnerMenuList(session),
    apiKey: 'SamsungDMPSegmentPartners',
    apiType: EReportFilterApiTypes.stringList,
    isApiKeyInReportFilterConfig: true,
  },
  {
    key: 'venue_ids',
    apiKey: 'VenueIDs',
    apiType: EReportFilterApiTypes.string,
  },
  {
    key: 'an_ids',
    apiKey: 'IASAnID',
    apiType: EReportFilterApiTypes.stringList,
  },
  {
    key: 'content_channel',
    apiKey: 'ContentChannelFilter',
    apiType: EReportFilterApiTypes.stringList,
    getList: (session: ISessionState, user: IUserState) =>
      getContentChannelFilterOptions(session, user),
  },
  {
    key: 'content_network',
    apiKey: 'ContentNetworkFilter',
    apiType: EReportFilterApiTypes.stringList,
    getList: (session: ISessionState, user: IUserState) =>
      getContentNetworkFilterOptions(session, user),
  },
  {
    key: 'orig_content_channel',
    apiKey: 'OrigContentChannelFilter',
    apiType: EReportFilterApiTypes.stringList,
    getList: (session: ISessionState, user: IUserState) =>
      getOrigContentChannelFilterOptions(session, user),
  },
  {
    key: 'orig_content_network',
    apiKey: 'OrigContentNetworkFilter',
    apiType: EReportFilterApiTypes.stringList,
    getList: (session: ISessionState, user: IUserState) =>
      getOrigContentNetworkFilterOptions(session, user),
  },
];
